import React, { useEffect, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import TableHeaders from '../../Interfaces/TableHeaders'
import TableHeader from '../../Table/TableHeader'
import { useTranslation } from 'react-i18next'
import { SnackbarSeverity } from '../../Alerts/SnackbarAlert'
import {
  Role,
  UserAccountDetails,
  UserRoleAssignment as UserRoleAssignmentSchema,
  UserAccountListing,
  Region,
} from '../../../swagger'
import { dateToSlashStringReinterpretedAsLocal } from '../../../utils/dateUtility'
import TextButton, { TextButtonVariant } from '../../Buttons/TextButton'
import TextField from '@mui/material/TextField'
import Header, { HeaderVariant } from '../../Elements/Header'
import RoleModal, { EditRoleArgs, AssignRoleArgs } from '../../Modals/RoleModal'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardFormHeader from '../../Card/CardFormHeader'
import ContainedButton, {
  ContainedButtonVariant,
} from '../../Buttons/ContainedButton'
import { useShowOnDesktop } from '../../../hooks/useShowOnDesktop'
import {
  extractedErrorObject,
  regionsApi,
  userAccountsApi,
} from '../../../api/swagger'
import ConfirmationModal from '../../Modals/ConfirmationModal'
import ActionButtons from '../../Buttons/ActionButtons'
import { Can } from '@casl/react'
import { useAuth } from '../../Routes/AuthProvider'
import { useSnackbarContext } from '../../Context/SnackbarContext'
import { OperationIds } from '../../../swagger/operationIdEnum'
import { LoadingContext } from '../../Context/LoadingContext'
import { useNavigate } from 'react-router'
import { styled } from '@mui/system'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { useMountEffect } from '../../../hooks/useMountEffect'

const filename = 'RolesTable'

const RolesCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    // Don't let the roles table get overlaid by buttons on mobile
    marginBottom: theme.spacing(12),
  },
}))

const RolesHeader = styled(Header)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginLeft: theme.spacing(3),
}))

const RolesTableCell = styled(TableCell)({
  minWidth: '200px',
})

interface RolesTableProps {
  roles?: UserRoleAssignment[]
  roleOptions?: Role[]
  userAccounts: UserAccountListing[]
  userKey: number
  userAccount: UserAccountDetails
  removeRole: () => void
  triggerRefresh?: () => void
}

export interface UserRoleAssignment
  extends Omit<UserRoleAssignmentSchema, 'validTo'> {
  validTo?: Date
}

export const RolesTable: React.FC<RolesTableProps> = ({
  roles,
  roleOptions,
  userKey,
  userAccount,
  userAccounts,
  removeRole,
  triggerRefresh,
}) => {
  const { permissionAbility } = useAuth()
  const { t } = useTranslation()
  const isShowOnDesktop = useShowOnDesktop()
  const { setSnackbarSeverity, setSnackbarMessage, setSnackbarState } =
    useSnackbarContext()
  const assignRoleLoadingId = `${filename}-${OperationIds.AssignRole}`
  const editRoleLoadingId = `${filename}-${OperationIds.EditRole}`
  const { removeLoadingIds } = React.useContext(LoadingContext)
  const navigate = useNavigate()

  const tableHeaders: TableHeaders[] = [
    {
      label: t('RolesTable.Header.Role', 'Role'),
      align: 'left',
    },
    {
      label: t('Team.Header.StartDate', 'Start Date'),
      align: 'left',
    },
    {
      label: t('Team.Header.End Date', 'End Date'),
      align: 'left',
    },
    {
      label: t('Team.Header.TeamLeader', 'Team Leader'),
      align: 'left',
    },
    {
      label: '',
      align: 'center',
      id: 'team-buttons-edit-remove',
    },
    {
      label: '',
      align: 'center',
      id: 'team-buttons-view-team',
    },
  ]

  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false)

  const errorMessage = t('UserAccount.Error', 'Something went wrong.')

  const [rolesRows, setRolesRows] = useState<UserRoleAssignment[]>([])

  const [rolesMap, setRolesMap] = useState(
    new Map<number /* actorKey */, UserRoleAssignment /* role information */>()
  )
  const [selectedRole, setSelectedRole] = useState<
    UserRoleAssignment | undefined
  >()

  const [roleChanged, setRoleChanged] = useState(true)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const { addLoadingIds } = React.useContext(LoadingContext)

  const availableLoadingIds = useLoadingIds()

  const [userRegionsForRoleModal, setUserRegionsForRoleModal] = useState<
    Region[]
  >([])

  const handleEditRoleClick = (actorKey: number) => {
    setIsRoleModalOpen(true)
    setSelectedRole(rolesMap.get(actorKey))
  }

  useMountEffect(() => {
    if (permissionAbility.can('view', 'Region')) {
      addLoadingIds([availableLoadingIds.Regions.fetchRegions])
    }
  })

  const fetchUserRegionsForRoleModal = async () => {
    try {
      const userRegionsForRoleModal = await regionsApi.fetchRegions({})

      setUserRegionsForRoleModal(userRegionsForRoleModal.regions)
    } catch (error) {
      const errorObject = (await extractedErrorObject(error)) ?? {
        code: 'Unknown',
        message: (error as unknown as Error).message ?? errorMessage,
      }
      setSnackbarState?.(true)
      setSnackbarMessage?.(errorObject.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
    }
  }

  useLoadingContext({
    asyncFunction: fetchUserRegionsForRoleModal,
    loadingId: availableLoadingIds.Regions.fetchRegions,
  })

  useEffect(() => {
    if (!!roles) {
      const map = new Map<
        number /* actorKey */,
        UserRoleAssignment /* role information */
      >()
      roles.forEach((role) => {
        map.set(role.actorKey, role)
      })
      setRolesMap(map)
    }
  }, [roles])

  useEffect(() => {
    if (!!rolesMap || !!roleChanged) {
      const rows: UserRoleAssignment[] = []
      rolesMap.forEach((role) => {
        rows.push(role)
      })
      setRolesRows(rows)
      setRoleChanged(false)
    }
  }, [rolesMap, roleChanged])

  const updateRoleForParent = (role: UserRoleAssignment) => {
    rolesMap.set(role.actorKey, role)
    setRolesMap(rolesMap)
    setRoleChanged(true)
    setIsRoleModalOpen((isOpen) => !isOpen)
  }

  const closeRoleModal = () => {
    setIsRoleModalOpen(false)
    setSelectedRole(undefined)
    triggerRefresh?.()
  }

  const [actorKeyToRemove, setActorKeyToRemove] = useState(-1)

  const handleRemoveRole = (actorKey: number) => {
    setActorKeyToRemove(actorKey)
    setSelectedRole(rolesMap.get(actorKey))
    setShowConfirmationModal(true)
  }

  const handleAcceptConfirmation = async (
    event: React.FormEvent<HTMLDivElement>
  ) => {
    event.preventDefault()
    const roleActorKey = userAccount.roles.filter(
      (role) => role.actorKey === actorKeyToRemove
    )
    try {
      await userAccountsApi.unassignRole({
        userKey,
        actorKey: roleActorKey[0].actorKey,
      })
      removeRole()
      setShowConfirmationModal(false)
      setSnackbarState?.(true)
      setSnackbarMessage?.(
        t(
          'RolesTable.RoleModal.ValidationMessage.Success',
          'Successfully removed role.'
        )
      )
      setSnackbarSeverity?.(SnackbarSeverity.Success)
    } catch (e) {
      const errorObj = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message:
          (e as unknown as Error).message ??
          t(
            'RolesTable.RoleModal.ValidationMessage.AddTransaction',
            'Something went wrong while removing the role.'
          ),
      }
      setSnackbarState?.(true)
      setSnackbarMessage?.(errorObj.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
    }
  }

  const handleEditRole = async ({
    actorKey,
    roleKey,
    supervisorActorKey,
    supervisorName,
    roleName,
    validFrom,
    validTo,
    regionKey,
    region,
  }: EditRoleArgs) => {
    try {
      await userAccountsApi.editRole({
        userKey,
        actorKey,
        body: {
          supervisorActorKey: supervisorActorKey as number, // we guarantee this is defined by disabling save button if it isn't
          validFrom,
          validTo,
          regionKey,
        },
      })

      setSnackbarState?.(true)
      setSnackbarMessage?.(
        t('Roles.EditRole.SuccessMessage', 'Role successfully updated.')
      )
      setSnackbarSeverity?.(SnackbarSeverity.Success)

      // We only update the changes made
      updateRoleForParent({
        actorKey,
        roleKey,
        region,
        regionKey,
        name: roleName,
        supervisor: supervisorName,
        validFrom,
        validTo,
      })
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message ?? errorMessage,
      }
      setSnackbarState?.(true)
      setSnackbarMessage?.(errorObject.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
    } finally {
      closeRoleModal()
    }
  }

  const handleAssignRole = async ({
    roleKey,
    supervisorActorKey,
    supervisorName,
    roleName,
    validFrom,
    validTo,
    regionKey,
    region,
  }: AssignRoleArgs) => {
    try {
      const response = await userAccountsApi.assignRole({
        roleKey,
        userKey,
        body: {
          supervisorActorKey: supervisorActorKey as number, // we guarantee this is defined by disabling save button if it isn't
          validFrom,
          validTo,
          regionKey,
        },
      })

      setSnackbarState?.(true)
      setSnackbarMessage?.(
        t('Roles.AssignRole.SuccessMessage', 'Role successfully assigned.')
      )
      setSnackbarSeverity?.(SnackbarSeverity.Success)

      updateRoleForParent({
        actorKey: response.actorKey,
        roleKey,
        name: roleName,
        region,
        regionKey,
        supervisor: supervisorName,
        validFrom,
        validTo,
      })
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message ?? errorMessage,
      }
      setSnackbarState?.(true)
      setSnackbarMessage?.(errorObject.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
    } finally {
      closeRoleModal()
    }
  }

  const headerMessage = `${t(
    'RolesTable.ConfirmationModal.Header.RemoveRole',
    'You are about to remove the role {{selectedRole}} for this user.',
    { selectedRole: selectedRole?.name, interpolation: { escapeValue: false } }
  )}`

  const confirmationBodyMessage = (
    <Typography variant="body1" component="p" align="center">
      {t(
        'ConfirmationModal.ConfirmationModal.Body',
        'This action is not reversible and by confirming “Yes” an email will be sent informing the user, team leaders, HR and Accounting that the role has been ended.'
      )}
    </Typography>
  )

  const handleConfirmationCancel = async () => {
    setShowConfirmationModal(false)
  }

  const confirmationButtons = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.YesRemove}
      secondaryButtonLabel={TextButtonVariant.NoCancel}
      secondaryClick={handleConfirmationCancel}
    />
  )

  const roleRemovalConfirmationProps = {
    isOpen: showConfirmationModal,
    dialogTitle: headerMessage,
    dialogContent: confirmationBodyMessage,
    dialogActions: confirmationButtons,
    handleFormSubmit: handleAcceptConfirmation,
  }

  const primaryButtonCallback = () => {
    !!selectedRole
      ? removeLoadingIds([editRoleLoadingId])
      : removeLoadingIds([assignRoleLoadingId])
  }

  return (
    <>
      <ConfirmationModal {...roleRemovalConfirmationProps} />
      <RoleModal
        regions={userRegionsForRoleModal}
        isOpen={isRoleModalOpen}
        onClose={closeRoleModal}
        initialRole={selectedRole}
        roleOptions={roleOptions}
        userAccounts={userAccounts}
        handleEditRole={handleEditRole}
        handleAssignRole={handleAssignRole}
        primaryButtonLoadingId={
          !!selectedRole ? editRoleLoadingId : assignRoleLoadingId
        }
        primaryButtonCallback={primaryButtonCallback}
      />

      <RolesCard>
        <CardFormHeader
          header={
            <RolesHeader
              id="rolesTableHeader"
              headerName={t('UserAccountCard.Header.Roles', 'Roles')}
              component="h3"
              variant={HeaderVariant.Card}
            />
          }
          buttons={
            <Can I={'accessRolesTab'} on="Admin" ability={permissionAbility}>
              <Box mr={2} mt={3} ml={isShowOnDesktop ? 0 : 2}>
                <ContainedButton
                  id="assignRoleToUserButton"
                  variant={ContainedButtonVariant.AssignRole}
                  fullWidth={!isShowOnDesktop}
                  onClick={() => setIsRoleModalOpen(true)}
                />
              </Box>
            </Can>
          }
          useDivider
        />
        <TableContainer>
          <Table aria-labelledby="rolesTableHeader">
            <TableHead>
              <TableHeader tableHeaders={tableHeaders} />
            </TableHead>
            <TableBody>
              {rolesRows
                // Sort by latest date (this prevents the table from looking scattered with the removed buttons)
                .sort(
                  (a, b) =>
                    +new Date(b.validTo ?? '') - +new Date(a.validTo ?? '')
                )
                .map((row) => (
                  <TableRow key={row.actorKey}>
                    <TableCell>
                      {row.name} - {row.actorKey}
                    </TableCell>
                    <RolesTableCell>
                      <TextField
                        id={`startDateField-${row.actorKey}`}
                        variant="filled"
                        /** If this TextField change its type, please use dateToDashString */
                        value={dateToSlashStringReinterpretedAsLocal(
                          row.validFrom
                        )}
                        type="text"
                        disabled
                        fullWidth
                      />
                    </RolesTableCell>
                    <RolesTableCell>
                      <TextField
                        id={`endDateField-${row.actorKey}`}
                        variant="filled"
                        /** If this TextField change its type, please use dateToDashString */
                        value={
                          row.validTo
                            ? dateToSlashStringReinterpretedAsLocal(row.validTo)
                            : ''
                        }
                        type="text"
                        disabled
                        fullWidth
                      />
                    </RolesTableCell>
                    <TableCell>
                      {row.supervisor ??
                        t('RolesTable.Supervisor.None', 'None')}
                    </TableCell>

                    <RolesTableCell>
                      {/** Hide buttons if role has been expired and actor does not have permission to edit expired roles*/}
                      {((row.validTo as Date) > new Date() ||
                        permissionAbility.can('editExpiredRoles', 'User')) && (
                        <Box display="flex">
                          <TextButton
                            id={`editRole-${row.actorKey}`}
                            variant={TextButtonVariant.Edit}
                            onClick={() => handleEditRoleClick(row.actorKey)}
                            fullWidth
                          />
                          <TextButton
                            id={`removeRole-${row.actorKey}`}
                            variant={TextButtonVariant.Remove}
                            onClick={() => handleRemoveRole(row.actorKey)}
                            fullWidth
                          />
                          {/* ???: This would be safer with an extra permission, such as view all user, but if a user is already within this account they can probably view their team. */}
                          {permissionAbility.can('view', 'User') && (
                            <ContainedButton
                              id="viewTeamButton"
                              variant={ContainedButtonVariant.ViewTeam}
                              onClick={() =>
                                navigate(
                                  {
                                    pathname: '/team',
                                  },
                                  {
                                    state: {
                                      userKey,
                                      actorKey: row.actorKey,
                                      firstName: userAccount.firstName,
                                      lastName: userAccount.lastName,
                                    },
                                  }
                                )
                              }
                              fullWidth={!isShowOnDesktop}
                            />
                          )}
                        </Box>
                      )}
                    </RolesTableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </RolesCard>
    </>
  )
}

export default RolesTable
